import type { EstimateDocContentBlock, EstimateDocLayout } from '@/types/estimateDocTemplate';
import { buildDefaultEstimateLayout } from '@/components/documents/estimates/templateBuilder/defaultLayout';

/**
 * The flat field-visibility view of a document DESIGN — the frontend twin of
 * the backend `docVisibilityFromLayout`, and the exact inverse of how the
 * default layout maps the flat booleans onto blocks.
 *
 * The design is the source of truth for what a document shows. The surfaces
 * that consume the flat shape (the single-scope page + PDF, the builder
 * preview, the file-name / expiry / offline-approval helpers) are fed from
 * here, so a design and its document can never disagree.
 *
 * Rules mirror the renderer one-to-one:
 * - a field prints unless its block sets `fields[key].visible === false`
 *   (`isFieldVisible`);
 * - a block-level toggle (`jobAddress` → serviceAddress, `notes` → notes) prints
 *   unless the block is `hidden`;
 * - a block the design does not carry shows nothing for its keys — the design
 *   deliberately has no such section.
 *
 * With no design at all (a caller that could not resolve one) the answer is
 * the STOCK DEFAULT design's — the same layout every null-layout surface
 * renders (`EstimateDocumentPdf`, the itemized page), so a file name or note
 * never carries a field the printed document hides.
 */
export interface DocFieldVisibility {
  companyAddress: boolean;
  companyPhone: boolean;
  companyEmail: boolean;
  customerEmail: boolean;
  customerPhone: boolean;
  serviceAddress: boolean;
  estimateNumber: boolean;
  date: boolean;
  validUntil: boolean;
  notes: boolean;
  itemDescriptions: boolean;
  itemImages: boolean;
  quantities: boolean;
  unitPrices: boolean;
}

export function layoutVisibility(
  layout: Pick<EstimateDocLayout, 'blocks'> | null | undefined,
): DocFieldVisibility {
  if (!layout || !Array.isArray(layout.blocks)) {
    return layoutVisibility(buildDefaultEstimateLayout());
  }
  const blocks = layout.blocks;
  const content = (type: string): EstimateDocContentBlock | undefined =>
    blocks.find((b): b is EstimateDocContentBlock => b.kind === 'content' && b.type === type);
  const field = (block: EstimateDocContentBlock | undefined, key: string) =>
    block != null && block.hidden !== true && block.fields?.[key]?.visible !== false;
  const shown = (block: EstimateDocContentBlock | undefined) =>
    block != null && block.hidden !== true;

  const company = content('companyInfo');
  const billTo = content('billTo');
  const details = content('estimateDetails');
  const items = content('itemGroups');

  return {
    companyAddress: field(company, 'address'),
    companyPhone: field(company, 'phone'),
    companyEmail: field(company, 'email'),
    customerEmail: field(billTo, 'email'),
    customerPhone: field(billTo, 'phone'),
    serviceAddress: shown(content('jobAddress')),
    estimateNumber: field(details, 'estimateNumber'),
    date: field(details, 'date'),
    validUntil: field(details, 'validUntil'),
    notes: shown(content('notes')),
    itemDescriptions: field(items, 'description'),
    itemImages: field(items, 'itemImage'),
    quantities: field(items, 'quantity'),
    unitPrices: field(items, 'unitPrice'),
  };
}
